import { useCallback, useState } from "react";
import { ResourceTypeLink } from "@evergis/api";

import { useCheckLimits } from "./useCheckLimits";
import { useTrigger } from "./useTrigger";
import { useLimitsExceededDialog } from "./useDialogs";

export const isInfinityLimit = (count: number) => count === -1;

interface DialogArgs {
  resources: string[];
  options?: Record<string, unknown>;
}

export const useLimitsExceededCheck = (
  defaultAction: (resources?: string[], options?: Record<string, unknown>) => void,
  checkTables = false,
): ((resources: string[], options?: Record<string, unknown>) => void) => {
  const openLimitsExceededDialog = useLimitsExceededDialog();
  const { limits, loading, refetch } = useCheckLimits(false);
  const [dialogArgs, setDialogArgs] = useState<DialogArgs>({ resources: [] });
  const [name, resourceType] = dialogArgs.resources || [];

  const onOpenDialog = useCallback(() => {
    if (!limits || !dialogArgs.resources.length) {
      return;
    }

    let isLimit = false;

    switch (resourceType) {
      case ResourceTypeLink.Project:
        isLimit = isInfinityLimit(limits.maxProjectsCount)
          ? false
          : limits.currentProjectCount >= limits.maxProjectsCount;
        break;
      case ResourceTypeLink.Layer:
        isLimit = isInfinityLimit(limits.maxServicesCount)
          ? false
          : limits.currentServiceCount >= limits.maxServicesCount;

        if (checkTables) {
          isLimit = isInfinityLimit(limits.maxTablesCount)
            ? false
            : isLimit || limits.currentTableCount >= limits.maxTablesCount;
        }
        break;
      case ResourceTypeLink.Table:
        isLimit = isInfinityLimit(limits.maxTablesCount) ? false : limits.currentTableCount >= limits.maxTablesCount;
        break;
      default:
        break;
    }

    if (isLimit) {
      openLimitsExceededDialog();
    } else {
      defaultAction?.(name !== null ? dialogArgs.resources : null, dialogArgs.options);
    }

    setDialogArgs({
      resources: [],
    });
  }, [limits, dialogArgs, resourceType, checkTables, openLimitsExceededDialog, defaultAction, name]);

  useTrigger(limits && !loading && dialogArgs.resources.length, onOpenDialog);

  return (dialogResources: string[], dialogOptions?: Record<string, unknown>) => {
    refetch().then(() => {
      setDialogArgs({
        resources: dialogResources,
        options: dialogOptions,
      });
    });
  };
};
